import React, { useEffect, useState } from 'react';
import { AdminLayout } from '../../components/AdminLayout';
import { Table } from '../../components/Table';
import { Modal } from '../../components/Modal';
import { PaginationControls } from '../../components/PaginationControls';
import { StatsCards } from '../../components/StatsCards';
import { FilterBar } from '../../components/FilterBar';
import { ramadanService, RamadanExpose } from '../../services/ramadanService';
import api from '../../config/api';

export const AdminRamadan: React.FC = () => {
  const [allExposes, setAllExposes] = useState<RamadanExpose[]>([]);
  const [exposes, setExposes] = useState<RamadanExpose[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedExpose, setSelectedExpose] = useState<RamadanExpose | null>(null);
  const [formData, setFormData] = useState<any>({ annee_ramadan: new Date().getFullYear() });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [stats, setStats] = useState({
    total: 0,
    a_venir: 0,
    en_cours: 0,
    termine: 0,
  });
  const [sectionStats, setSectionStats] = useState<{ VCN: number; HDR: number; SANTE: number }>({
    VCN: 0,
    HDR: 0,
    SANTE: 0,
  });
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    section: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    fetchExposes(1);
    fetchStats();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [filters, allExposes, currentPage]);

  const fetchStats = async () => {
    try {
      const allData = await ramadanService.getAll({ page_size: 1000 });
      const allExposes = allData.results || [];
      
      const statsData = {
        total: allData.count || allExposes.length,
        a_venir: allExposes.filter((e: RamadanExpose) => e.status_auto === 'a_venir').length,
        en_cours: allExposes.filter((e: RamadanExpose) => e.status_auto === 'en_cours').length,
        termine: allExposes.filter((e: RamadanExpose) => e.status_auto === 'termine').length,
      };
      setStats(statsData);

      const sectionCounts = { VCN: 0, HDR: 0, SANTE: 0 };
      allExposes.forEach((expose: RamadanExpose) => {
        if (expose.section === 'VCN') {
          sectionCounts.VCN += 1;
        } else if (expose.section === 'HDR') {
          sectionCounts.HDR += 1;
        } else if (expose.section === 'SANTE') {
          sectionCounts.SANTE += 1;
        }
      });
      setSectionStats(sectionCounts);
    } catch (error) {
      console.error('Erreur lors du chargement des stats:', error);
    }
  };

  const fetchExposes = async (page: number = 1) => {
    setLoading(true);
    try {
      const data = await ramadanService.getAll({ page_size: 1000 });
      const allExposesData = data.results || [];
      setAllExposes(allExposesData);
      setTotalCount(allExposesData.length);
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...allExposes];

    // Filtre par recherche
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter((expose) =>
        expose.theme?.toLowerCase().includes(searchLower) ||
        expose.nom_exposant?.toLowerCase().includes(searchLower) ||
        expose.prenom_exposant?.toLowerCase().includes(searchLower) ||
        expose.classe?.toLowerCase().includes(searchLower)
      );
    }

    // Filtre par statut
    if (filters.status) {
      if (filters.status === 'a_venir') {
        filtered = filtered.filter((expose) => expose.status_auto === 'a_venir');
      } else if (filters.status === 'en_cours') {
        filtered = filtered.filter((expose) => expose.status_auto === 'en_cours');
      } else if (filters.status === 'termine') {
        filtered = filtered.filter((expose) => expose.status_auto === 'termine');
      }
    }

    // Filtre par section
    if (filters.section) {
      filtered = filtered.filter((expose) => expose.section === filters.section);
    }

    // Trier par date de création décroissante (plus récent en premier)
    filtered.sort((a, b) => {
      const dateA = new Date((a as any).created_at || a.date_expose || 0).getTime();
      const dateB = new Date((b as any).created_at || b.date_expose || 0).getTime();
      return dateB - dateA;
    });

    // Pagination
    const pageSize = 6;
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedExposes = filtered.slice(startIndex, endIndex);

    setExposes(paginatedExposes);
    setTotalCount(filtered.length);
    setTotalPages(Math.ceil(filtered.length / pageSize));
  };

  const handleCreate = () => {
    setFormData({ section: 'VCN', annee_ramadan: new Date().getFullYear() });
    setSelectedExpose(null);
    setIsModalOpen(true);
  };

  const handleEdit = (expose: RamadanExpose) => {
    setFormData({
      nom_exposant: expose.nom_exposant,
      prenom_exposant: expose.prenom_exposant,
      telephone_exposant: expose.telephone_exposant,
      classe: expose.classe,
      theme: expose.theme,
      description: expose.description,
      section: expose.section || 'VCN',
      date_expose: expose.date_expose.split('T')[0],
      jour_ramadan: expose.jour_ramadan,
      annee_ramadan: expose.annee_ramadan,
      lien_youtube: expose.lien_youtube || '',
      lien_tiktok: expose.lien_tiktok || '',
      lien_instagram: expose.lien_instagram || '',
      status: expose.status,
      photo_exposant: null,
      mini_video: null,
      audio_complet: null,
      fichier_pdf: null,
    });
    setSelectedExpose(expose);
    setIsModalOpen(true);
  };

  const handleView = (expose: RamadanExpose) => {
    setSelectedExpose(expose);
    setIsViewModalOpen(true);
  };

  const handleDelete = (expose: RamadanExpose) => {
    setSelectedExpose(expose);
    setIsDeleteModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return; // Empêcher les doubles clics
    
    setIsSubmitting(true);
    try {
      const formDataToSend = new FormData();
      Object.keys(formData).forEach((key) => {
        if (formData[key] !== null && formData[key] !== undefined && formData[key] !== '') {
          // Si c'est un fichier, l'ajouter directement
          if ((key === 'photo_exposant' || key === 'mini_video' || key === 'audio_complet' || key === 'fichier_pdf') && formData[key] instanceof File) {
            formDataToSend.append(key, formData[key]);
          } else if (key !== 'photo_exposant' && key !== 'mini_video' && key !== 'audio_complet' && key !== 'fichier_pdf') {
            // Pour les autres champs, seulement si ce n'est pas un fichier
            formDataToSend.append(key, formData[key]);
          }
        }
      });

      if (selectedExpose) {
        await api.put(`/ramadan/exposes/${selectedExpose.id}/`, formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        await api.post('/ramadan/exposes/', formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      setIsModalOpen(false);
      await fetchExposes(currentPage);
      fetchStats();
    } catch (error) {
      console.error('Erreur:', error);
      alert('Une erreur est survenue');
    } finally {
      setIsSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!selectedExpose || isDeleting) return;
    setIsDeleting(true);
    try {
      await api.delete(`/ramadan/exposes/${selectedExpose.id}/`);
      setIsDeleteModalOpen(false);
      await fetchExposes(currentPage);
      fetchStats();
    } catch (error) {
      console.error('Erreur:', error);
      alert('Une erreur est survenue');
    } finally {
      setIsDeleting(false);
    }
  };

  const columns = [
    { key: 'id', label: 'ID' },
    { 
      key: 'theme', 
      label: 'Thème',
      render: (value: string) => <strong className="text-green-700">{value}</strong>
    },
    {
      key: 'prenom_exposant',
      label: 'Exposant',
      render: (_: any, row: RamadanExpose) => (
        <span className="font-medium">{row.prenom_exposant} {row.nom_exposant}</span>
      )
    },
    {
      key: 'section',
      label: 'Section',
      render: (value: string) => {
        const sections: { [key: string]: string } = {
          VCN: 'VCN (Université)',
          HDR: 'HDR',
          SANTE: 'SANTÉ',
        };
        return (
          <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-semibold">
            {sections[value] || value}
          </span>
        );
      }
    },
    {
      key: 'date_expose',
      label: 'Date',
      render: (value: string) => (
        <span className="text-gray-600">{new Date(value).toLocaleDateString('fr-FR')}</span>
      )
    },
    {
      key: 'jour_ramadan',
      label: 'Jour',
      render: (value: number) => value ? (
        <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded-full text-xs font-semibold">
          Jour {value}
        </span>
      ) : '-'
    },
    {
      key: 'annee_ramadan',
      label: 'Année',
      render: (value: number) => (
        <span className="font-semibold">{value}</span>
      )
    },
  ];

  if (loading) {
    return (
      <AdminLayout activeSection="ramadan">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-700"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout activeSection="ramadan">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-3xl font-bold text-green-700">Gestion des Exposés Ramadan</h2>
          <p className="text-gray-600 mt-1">Créez et gérez tous les exposés du Ramadan</p>
        </div>
        <button onClick={handleCreate} className="btn-primary text-lg px-6 py-3">
          + Nouvel Exposé Ramadan
        </button>
      </div>

      {/* Cartes de statistiques */}
      <StatsCards
        stats={[
          { label: 'Total Exposés', value: stats.total, icon: '🌙', color: 'purple' },
          { label: 'À venir', value: stats.a_venir, icon: '⏳', color: 'amber' },
          { label: 'En cours', value: stats.en_cours, icon: '🟢', color: 'green' },
          { label: 'Terminés', value: stats.termine, icon: '✅', color: 'indigo' },
        ]}
      />
      <StatsCards
        stats={[
          { label: 'Section VCN', value: sectionStats.VCN, icon: '🎓', color: 'green' },
          { label: 'Section HDR', value: sectionStats.HDR, icon: '🏛️', color: 'purple' },
          { label: 'Section SANTÉ', value: sectionStats.SANTE, icon: '🩺', color: 'teal' },
        ]}
      />

      {/* Filtres */}
      <FilterBar
        searchPlaceholder="🔍 Rechercher par thème, exposant, classe..."
        searchValue={filters.search}
        onSearchChange={(value) => {
          setFilters({ ...filters, search: value });
          setCurrentPage(1);
        }}
        filters={[
          {
            key: 'status',
            label: 'Tous les statuts',
            value: filters.status,
            onChange: (value) => {
              setFilters({ ...filters, status: value });
              setCurrentPage(1);
            },
            options: [
              { value: 'a_venir', label: '⏳ À venir' },
              { value: 'en_cours', label: '🟢 En cours' },
              { value: 'termine', label: '✅ Terminés' },
            ],
          },
          {
            key: 'section',
            label: 'Toutes les sections',
            value: filters.section,
            onChange: (value) => {
              setFilters({ ...filters, section: value });
              setCurrentPage(1);
            },
            options: [
              { value: 'VCN', label: '🎓 VCN (Université)' },
              { value: 'HDR', label: '🏛️ HDR' },
              { value: 'SANTE', label: '🩺 SANTÉ' },
            ],
          },
        ]}
        onClear={() => {
          setFilters({ search: '', status: '', section: '' });
          setCurrentPage(1);
        }}
      />

      <Table
        columns={columns}
        data={exposes}
        onView={handleView}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />

      <PaginationControls
        currentPage={currentPage}
        totalPages={totalPages}
        loading={loading}
        onPageChange={(page) => {
          setCurrentPage(page);
        }}
      />

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={selectedExpose ? 'Modifier l\'exposé Ramadan' : 'Nouvel exposé Ramadan'}
        size="lg"
        footer={
          <div className="flex gap-4">
            <button 
              onClick={() => setIsModalOpen(false)} 
              className="btn-secondary"
              disabled={isSubmitting}
            >
              Annuler
            </button>
            <button 
              onClick={handleSubmit} 
              className="btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                  Enregistrement...
                </>
              ) : (
                'Enregistrer'
              )}
            </button>
          </div>
        }
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Prénom exposant *</label>
              <input
                type="text"
                className="input-field"
                value={formData.prenom_exposant || ''}
                onChange={(e) => setFormData({ ...formData, prenom_exposant: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Nom exposant *</label>
              <input
                type="text"
                className="input-field"
                value={formData.nom_exposant || ''}
                onChange={(e) => setFormData({ ...formData, nom_exposant: e.target.value })}
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Téléphone</label>
              <input
                type="tel"
                className="input-field"
                value={formData.telephone_exposant || ''}
                onChange={(e) => setFormData({ ...formData, telephone_exposant: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Classe *</label>
              <input
                type="text"
                className="input-field"
                value={formData.classe || ''}
                onChange={(e) => setFormData({ ...formData, classe: e.target.value })}
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Thème *</label>
            <input
              type="text"
              className="input-field"
              value={formData.theme || ''}
              onChange={(e) => setFormData({ ...formData, theme: e.target.value })}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
            <textarea
              className="input-field min-h-[100px]"
              value={formData.description || ''}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Section Hanafia *</label>
              <select
                className="input-field"
                value={formData.section || 'VCN'}
                onChange={(e) => setFormData({ ...formData, section: e.target.value })}
                required
              >
                <option value="VCN">VCN (Université)</option>
                <option value="HDR">HDR</option>
                <option value="SANTE">SANTÉ</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Statut</label>
              <select
                className="input-field"
                value={formData.status || 'a_venir'}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              >
                <option value="a_venir">À venir</option>
                <option value="passe">Passé</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Date *</label>
              <input
                type="date"
                className="input-field"
                value={formData.date_expose || ''}
                onChange={(e) => setFormData({ ...formData, date_expose: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Jour Ramadan</label>
              <input
                type="number"
                className="input-field"
                value={formData.jour_ramadan || ''}
                onChange={(e) => setFormData({ ...formData, jour_ramadan: parseInt(e.target.value) || '' })}
                min="1"
                max="30"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Année *</label>
              <input
                type="number"
                className="input-field"
                value={formData.annee_ramadan || new Date().getFullYear()}
                onChange={(e) => setFormData({ ...formData, annee_ramadan: parseInt(e.target.value) })}
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">YouTube</label>
              <input
                type="url"
                className="input-field"
                value={formData.lien_youtube || ''}
                onChange={(e) => setFormData({ ...formData, lien_youtube: e.target.value })}
                placeholder="https://..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">TikTok</label>
              <input
                type="url"
                className="input-field"
                value={formData.lien_tiktok || ''}
                onChange={(e) => setFormData({ ...formData, lien_tiktok: e.target.value })}
                placeholder="https://..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Instagram</label>
              <input
                type="url"
                className="input-field"
                value={formData.lien_instagram || ''}
                onChange={(e) => setFormData({ ...formData, lien_instagram: e.target.value })}
                placeholder="https://..."
              />
            </div>
          </div>

          {/* Photo exposant */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              📷 Photo de l'exposant {selectedExpose && selectedExpose.photo_exposant && '(actuelle)'}
            </label>
            {selectedExpose && selectedExpose.photo_exposant && (
              <div className="mb-2">
                <img 
                  src={selectedExpose.photo_exposant} 
                  alt="Photo actuelle"
                  className="w-20 h-20 object-cover rounded-lg border-2 border-purple-300 shadow-md"
                />
              </div>
            )}
            <input
              type="file"
              accept="image/*"
              className="input-field"
              onChange={(e) => {
                if (e.target.files && e.target.files[0]) {
                  setFormData({ ...formData, photo_exposant: e.target.files[0] });
                }
              }}
            />
            <p className="text-xs text-gray-500 mt-1">Formats acceptés: JPG, PNG, GIF (max 5MB)</p>
          </div>

          {/* Mini Vidéo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              🎬 Mini Vidéo (2-5 min) {selectedExpose && selectedExpose.mini_video && '(actuelle)'}
            </label>
            {selectedExpose && selectedExpose.mini_video && (
              <div className="mb-2">
                <video 
                  src={selectedExpose.mini_video} 
                  controls 
                  className="w-full max-w-md rounded-lg border-2 border-purple-300"
                />
              </div>
            )}
            <input
              type="file"
              accept="video/*"
              className="input-field"
              onChange={(e) => {
                if (e.target.files && e.target.files[0]) {
                  setFormData({ ...formData, mini_video: e.target.files[0] });
                }
              }}
            />
            <p className="text-xs text-gray-500 mt-1">Formats acceptés: MP4, MOV, AVI (max 50MB)</p>
          </div>

          {/* Audio Complet */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              🎧 Audio Complet {selectedExpose && selectedExpose.audio_complet && '(actuel)'}
            </label>
            {selectedExpose && selectedExpose.audio_complet && (
              <div className="mb-2">
                <audio 
                  src={selectedExpose.audio_complet} 
                  controls 
                  className="w-full max-w-md"
                />
              </div>
            )}
            <input
              type="file"
              accept="audio/*"
              className="input-field"
              onChange={(e) => {
                if (e.target.files && e.target.files[0]) {
                  setFormData({ ...formData, audio_complet: e.target.files[0] });
                }
              }}
            />
            <p className="text-xs text-gray-500 mt-1">Formats acceptés: MP3, WAV, OGG (max 50MB)</p>
          </div>

          {/* Fichier PDF */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              📄 Fichier PDF {selectedExpose && selectedExpose.fichier_pdf && '(actuel)'}
            </label>
            {selectedExpose && selectedExpose.fichier_pdf && (
              <div className="mb-2">
                <a 
                  href={selectedExpose.fichier_pdf} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-purple-700 hover:text-purple-800 font-semibold underline"
                >
                  📄 Voir le PDF actuel
                </a>
              </div>
            )}
            <input
              type="file"
              accept=".pdf"
              className="input-field"
              onChange={(e) => {
                if (e.target.files && e.target.files[0]) {
                  setFormData({ ...formData, fichier_pdf: e.target.files[0] });
                }
              }}
            />
            <p className="text-xs text-gray-500 mt-1">Format accepté: PDF (max 10MB)</p>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        title="Détails de l'exposé Ramadan"
      >
        {selectedExpose && (
          <div className="space-y-6">
            {/* Photo exposant */}
            {selectedExpose.photo_exposant && (
              <div className="flex justify-center mb-4">
                <div className="relative">
                  <img 
                    src={selectedExpose.photo_exposant} 
                    alt={`${selectedExpose.prenom_exposant} ${selectedExpose.nom_exposant}`}
                    className="w-32 h-32 object-cover rounded-full border-4 border-purple-500 shadow-xl"
                  />
                  <div className="absolute -bottom-2 -right-2 bg-purple-600 text-white rounded-full p-2 text-xl">
                    📷
                  </div>
                </div>
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600 mb-1">Thème</p>
                <p className="font-semibold text-lg text-purple-700">{selectedExpose.theme}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Exposant</p>
                <p className="font-semibold text-lg">{selectedExpose.prenom_exposant} {selectedExpose.nom_exposant}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Classe</p>
                <p className="font-semibold">{selectedExpose.classe}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Section Hanafia</p>
                <p className="font-semibold">
                  {selectedExpose.section === 'VCN' ? 'VCN (Université)' :
                   selectedExpose.section === 'HDR' ? 'HDR' :
                   selectedExpose.section === 'SANTE' ? 'SANTÉ' :
                   selectedExpose.section}
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Téléphone</p>
                <p className="font-semibold">{selectedExpose.telephone_exposant || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Date</p>
                <p className="font-semibold">{new Date(selectedExpose.date_expose).toLocaleDateString('fr-FR')}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Jour Ramadan</p>
                <p className="font-semibold">{selectedExpose.jour_ramadan ? `Jour ${selectedExpose.jour_ramadan}` : '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Année Ramadan</p>
                <p className="font-semibold">{selectedExpose.annee_ramadan}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Statut</p>
                <p className="font-semibold">
                  {selectedExpose.status_auto === 'a_venir' ? '⏳ À venir' :
                   selectedExpose.status_auto === 'en_cours' ? '🟢 En cours' :
                   selectedExpose.status_auto === 'termine' ? '✅ Terminé' :
                   selectedExpose.status}
                </p>
              </div>
            </div>
            {selectedExpose.description && (
              <div>
                <p className="text-sm text-gray-600 mb-2">Description</p>
                <p className="font-semibold whitespace-pre-wrap break-words bg-gray-50 p-3 rounded-lg">{selectedExpose.description}</p>
              </div>
            )}
            {(selectedExpose.lien_youtube || selectedExpose.lien_tiktok || selectedExpose.lien_instagram) && (
              <div>
                <p className="text-sm text-gray-600 mb-2">Liens sociaux</p>
                <div className="flex flex-wrap gap-2">
                  {selectedExpose.lien_youtube && (
                    <a href={selectedExpose.lien_youtube} target="_blank" rel="noopener noreferrer" className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm hover:bg-red-200">
                      📺 YouTube
                    </a>
                  )}
                  {selectedExpose.lien_tiktok && (
                    <a href={selectedExpose.lien_tiktok} target="_blank" rel="noopener noreferrer" className="bg-gray-100 text-gray-800 px-3 py-1 rounded-full text-sm hover:bg-gray-200">
                      🎵 TikTok
                    </a>
                  )}
                  {selectedExpose.lien_instagram && (
                    <a href={selectedExpose.lien_instagram} target="_blank" rel="noopener noreferrer" className="bg-pink-100 text-pink-800 px-3 py-1 rounded-full text-sm hover:bg-pink-200">
                      📷 Instagram
                    </a>
                  )}
                </div>
              </div>
            )}
            {(selectedExpose.mini_video || selectedExpose.audio_complet || selectedExpose.fichier_pdf) && (
              <div>
                <p className="text-sm text-gray-600 mb-2">Médias</p>
                <div className="space-y-2">
                  {selectedExpose.mini_video && (
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Mini Vidéo</p>
                      <video src={selectedExpose.mini_video} controls className="w-full max-w-md rounded-lg" />
                    </div>
                  )}
                  {selectedExpose.audio_complet && (
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Audio Complet</p>
                      <audio src={selectedExpose.audio_complet} controls className="w-full max-w-md" />
                    </div>
                  )}
                  {selectedExpose.fichier_pdf && (
                    <div>
                      <a href={selectedExpose.fichier_pdf} target="_blank" rel="noopener noreferrer" className="text-purple-700 hover:text-purple-800 font-semibold underline">
                        📄 Voir le PDF
                      </a>
                    </div>
                  )}
                </div>
              </div>
            )}
            <div>
              <p className="text-sm text-gray-600">Statistiques</p>
              <div className="flex gap-4 mt-2">
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full">👁️ {selectedExpose.nombre_vues} vues</span>
                <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full">❤️ {selectedExpose.nombre_likes} likes</span>
                <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full">💬 {selectedExpose.nombre_commentaires || 0} commentaires</span>
              </div>
            </div>
            <div className="pt-4 border-t">
              <p className="text-xs text-gray-500">
                Créé le {new Date(selectedExpose.created_at).toLocaleString('fr-FR')}
              </p>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Confirmer la suppression"
        footer={
          <div className="flex gap-4">
            <button 
              onClick={() => setIsDeleteModalOpen(false)} 
              className="btn-secondary"
              disabled={isDeleting}
            >
              Annuler
            </button>
            <button 
              onClick={confirmDelete} 
              className="btn-danger"
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <span className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                  Suppression...
                </>
              ) : (
                'Supprimer'
              )}
            </button>
          </div>
        }
      >
        <p className="text-lg">Êtes-vous sûr de vouloir supprimer l'exposé Ramadan <strong>"{selectedExpose?.theme}"</strong> ?</p>
      </Modal>
    </AdminLayout>
  );
};
